/**
 * Payouts API Service
 *
 * Handles all payout-related API calls:
 * - Get payout settings
 * - Update payout settings
 * - Get banks list
 * - Get payout history
 * - Request payout
 */

import { post, get } from './client';

export const payoutsApi = {
    /**
     * Get payout settings for a landlord
     * @param {string} landlordId - Landlord ID
     * @returns {Promise<Object>} Payout settings including balances
     */
    getSettings: async (landlordId) => {
        return post('pegasus/visionary/landlord/payout/settings', {
            landlord_id: landlordId,
        });
    },

    /**
     * Update payout settings
     * @param {Object} data - Settings data
     * @param {string} data.landlord_id - Landlord ID
     * @param {string} data.payout_method - 'BANK' or 'MPESA'
     * @param {string} [data.bank_code] - Bank code (for BANK method)
     * @param {string} [data.account_number] - Bank account number (for BANK method)
     * @param {string} [data.account_name] - Bank account name (for BANK method)
     * @param {string} [data.phone_number] - M-PESA phone number (for MPESA method)
     * @param {string} [data.registered_name] - M-PESA registered name (for MPESA method)
     * @param {boolean} [data.auto_disburse] - Enable auto-disbursement
     * @param {number} [data.minimum_payout] - Minimum payout amount
     * @returns {Promise<Object>} Update response
     */
    updateSettings: async (data) => {
        return post('pegasus/visionary/landlord/payout/settings/update', data);
    },

    /**
     * Get list of available banks
     * @returns {Promise<Object>} List of banks
     */
    getBanks: async () => {
        return get('pegasus/visionary/landlord/payout/banks');
    },

    /**
     * Get payout history for a landlord
     * @param {string} landlordId - Landlord ID
     * @param {number} [page=1] - Page number
     * @param {number} [limit=10] - Items per page
     * @returns {Promise<Object>} Payout history with pagination
     */
    getHistory: async (landlordId, page = 1, limit = 10) => {
        return post('pegasus/visionary/landlord/payout/history', {
            landlord_id: landlordId,
            page,
            limit,
        });
    },

    /**
     * Request a manual payout
     * @param {string} landlordId - Landlord ID
     * @param {number} [amount] - Amount to request (optional, defaults to full balance)
     * @returns {Promise<Object>} Payout request response
     */
    requestPayout: async (landlordId, amount) => {
        const body = { landlord_id: landlordId };
        if (amount !== undefined && amount !== null) {
            body.amount = amount;
        }
        return post('pegasus/visionary/landlord/payout/request', body);
    },
};

export default payoutsApi;
